/**
 * frontend/src/context/AuthContext.jsx
 * ======================================
 * Provides auth state to the manager dashboard.
 *
 * IMPORTANT — sessionStorage, not localStorage
 * The manager dashboard is embedded as an iframe inside Commerce7 admin.
 * localStorage is blocked or unreliable in cross-origin iframes on Safari.
 * sessionStorage works because the iframe has its own browsing context.
 *
 * The /checkin surface is a separate route that never uses this context —
 * it reads its token directly from the URL query param.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react'
import { authApi } from '../lib/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null)
  const [loading, setLoading] = useState(true)

  // On mount, verify the stored token is still valid
  useEffect(() => {
    const init = async () => {
      if (!authApi.isAuthenticated()) {
        setLoading(false)
        return
      }
      try {
        const res = await authApi.me()
        setUser(res.data)
      } catch {
        // Token expired or revoked — clear it silently
        authApi.clearToken()
        setUser(null)
      } finally {
        setLoading(false)
      }
    }
    init()
  }, [])

  const login = useCallback(async (email, password) => {
    const res = await authApi.login(email, password)
    const { access_token } = res.data
    authApi.setToken(access_token)
    // Fetch user profile
    const meRes = await authApi.me()
    setUser(meRes.data)
    return meRes.data
  }, [])

  const logout = useCallback(() => {
    authApi.clearToken()
    setUser(null)
  }, [])

  return (
    <AuthContext.Provider value={{ user, loading, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
